import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Card, CardBody, CardHeader } from '@/components/ui/card';
import { Switch } from '@/components/ui/menu';
import { Skeleton } from '@/components/ui/skeleton';
import { REMINDER_LABEL, reminderWhen } from '@/features/settings/RemindersCard';
import { useInvoiceReminders, useSetInvoiceRemindersPaused } from '@/hooks/useSettings';
import { formatDate, toDayString } from '@/lib/format';
import type { InvoiceStatus } from '@/types';

/**
 * This invoice's automatic reminders: what has gone out, what is next, and a
 * pause switch for the client who has promised to pay on Friday.
 */
export function InvoiceRemindersCard({
  invoiceId,
  status
}: {
  invoiceId: string;
  status: InvoiceStatus | undefined;
}) {
  const reminders = useInvoiceReminders(invoiceId);
  const setPaused = useSetInvoiceRemindersPaused(invoiceId);
  const data = reminders.data;
  // Dates that passed before reminders were switched on are not news.
  const schedule = data?.schedule.filter((r) => r.status !== 'missed') ?? [];
  const done = status === 'paid' || status === 'archived';

  // A paid invoice that never had a reminder has nothing to say here.
  if (done && !data?.schedule.some((r) => r.sentAt)) return null;

  return (
    <Card>
      <CardHeader
        title="Reminders"
        actions={
          data && schedule.length && !done ? (
            <label className="flex items-center gap-2 text-sm text-muted">
              Pause
              <Switch
                checked={data.paused}
                disabled={setPaused.isPending}
                onCheckedChange={(paused) => setPaused.mutate(paused)}
                aria-label="Pause reminders for this invoice"
              />
            </label>
          ) : null
        }
      />
      <CardBody className="space-y-3">
        {reminders.isPending || !data ? (
          <Skeleton className="h-16" />
        ) : !schedule.length ? (
          <p className="text-sm text-muted">
            {data.schedule.length ? 'No more reminders scheduled.' : 'Automatic reminders are off.'}{' '}
            <Link to="/invoice-settings" className="text-[var(--accent)] hover:underline">
              {data.schedule.length ? 'Reminder settings' : 'Turn them on'}
            </Link>
          </p>
        ) : (
          <>
            <ul className="space-y-2 text-sm">
              {schedule.map((r) => (
                <li key={`${r.kind}-${r.offsetDays}`} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="block">{REMINDER_LABEL[r.kind]}</span>
                    <span className="block text-muted">
                      {r.sentAt
                        ? `Sent ${formatDate(toDayString(new Date(r.sentAt)))}`
                        : `${formatDate(r.date)} · ${reminderWhen(r.offsetDays)}`}
                    </span>
                  </span>
                  {r.status === 'sent' ? (
                    <Badge tone="positive">Sent</Badge>
                  ) : data.paused || !data.eligible ? (
                    <Badge tone="neutral">On hold</Badge>
                  ) : (
                    <Badge tone="accent">Scheduled</Badge>
                  )}
                </li>
              ))}
            </ul>
            {!data.eligible && !done ? (
              <p className="text-sm text-muted">
                {status === 'sent'
                  ? 'On hold while a bank transfer clears.'
                  : 'Reminders start once the invoice is sent.'}
              </p>
            ) : data.paused ? (
              <p className="text-sm text-muted">Paused. Nothing goes out until you resume.</p>
            ) : null}
          </>
        )}
      </CardBody>
    </Card>
  );
}
